import React, { useState, useEffect, useRef, useMemo } from "react";
import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  Sidebar,
  ConversationList,
  Conversation,
  InputToolbox,
  AttachmentButton,
} from "@chatscope/chat-ui-kit-react";
import styles from "./Chat.module.css";
import config from "../config";
import { useUser, ChatUser, MessageType } from "../contexts/UserContext";
import EditUser from "../components/chat/EditUser";
import EditButton from "../components/chat/EditButton";
import { BarLoader, PuffLoader } from "react-spinners";
import { useChat } from "../contexts/ChatContext";
import useIsMobile from "../hooks/useIsMobile";
import MessageActionMenu, {
  MessageAction,
} from "../components/chat/MessageActionMenu";
import LongPressMessageWrapper from "../components/chat/LongPressMessageWrapper";
import copyToClipboard from "../utils/copyToClipboard";

function getDateKey(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function formatMessageDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat("he-IL", { dateStyle: "medium" }).format(
      date
    );
  } catch {
    return "";
  }
}

function formatMessageTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "";
  }
}

const Chat = () => {
  const {
    usersWithActiveChat,
    setUsersWithActiveChat,
    usersWithChatHistory,
    setUsersWithChatHistory,
    isActiveChat,
    setIsActiveChat,
    getUsersWithActiveChat,
    getUsersWithChatHistory,
    loading,
  } = useUser();
  const [messageInput, setMessageInput] = useState("");
  const [currentUser, setCurrentUser] = useState<{
    name?: string;
    username?: string;
  } | null>(null);

  // Remove local isActiveChat and editUserOpenId fetch logic
  const [editUserOpenId, setEditUserOpenId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const [showImage, setShowImage] = useState(false);
  const isMobile = useIsMobile();
  const [actionAnchor, setActionAnchor] = useState<{
    rect: DOMRect;
    msg: MessageType;
  } | null>(null);

  const {
    mobileSidebarOpen,
    setMobileSidebarOpen,
    toggleMobileSidebar,
    selectedUserId,
    setSelectedUserId,
  } = useChat();

  useEffect(() => {
    // scroll to top of the page
    window.scrollTo(0, 0);
    // Only set mobile sidebar open on mobile - on desktop chat should always be visible
    if (window.innerWidth <= 768) {
      setMobileSidebarOpen(true);
    }
  }, [setMobileSidebarOpen]);

  useEffect(() => {
    if (enlargedImage) {
      setShowImage(false);
      const timeout = setTimeout(() => setShowImage(true), 10);
      return () => clearTimeout(timeout);
    } else {
      setShowImage(false);
    }
  }, [enlargedImage]);

  useEffect(() => {
    //scroll to top of the page
    window.scrollTo(0, 0);
    const fetchUsers = async () => {
      if (isActiveChat) {
        const users = await getUsersWithActiveChat();
        setUsersWithActiveChat(users);
        if (users.length > 0) {
          setSelectedUserId((prev) =>
            prev && users.some((u) => u._id === prev)
              ? prev
              : users[0]._id ?? null
          );
        } else {
          setSelectedUserId(null);
        }
      } else {
        const users = await getUsersWithChatHistory();
        setUsersWithChatHistory(users);
        if (users.length > 0) {
          setSelectedUserId((prev) =>
            prev && users.some((u) => u._id === prev)
              ? prev
              : users[0]._id ?? null
          );
        } else {
          setSelectedUserId(null);
        }
      }
    };
    fetchUsers();
  }, [
    isActiveChat,
    getUsersWithActiveChat,
    getUsersWithChatHistory,
    setUsersWithActiveChat,
    setUsersWithChatHistory,
    setSelectedUserId,
  ]);

  // Fetch current user info on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      // 1. Get userId from validate-token
      const res = await fetch(`${config.backendUrl}/users/validate-token`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!data.userId) return;
      // 2. Get user info from get-users (by id)
      const usersRes = await fetch(
        `${config.backendUrl}/users/get-users?phone=`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      const usersData = await usersRes.json();
      if (usersData.users && Array.isArray(usersData.users)) {
        const found = usersData.users.find((u: any) => u._id === data.userId);
        if (found)
          setCurrentUser({ name: found.name, username: found.username });
      }
    };
    fetchCurrentUser();
  }, []);

  // Determine which user list to use based on isActiveChat
  const userList = isActiveChat ? usersWithActiveChat : usersWithChatHistory;
  const selectedUser = userList.find((user) => user._id === selectedUserId);
  const messages = selectedUser ? selectedUser.messages : [];
  const sortedHistoryUsers = useMemo(() => {
    const getLatestMessageTime = (user: ChatUser) => {
      if (!Array.isArray(user.messages) || user.messages.length === 0) return 0;
      return user.messages.reduce((maxTs, msg) => {
        const ts = new Date(msg.sentTime || 0).getTime();
        return Number.isNaN(ts) ? maxTs : Math.max(maxTs, ts);
      }, 0);
    };
    const sorted = [...usersWithChatHistory].sort(
      (a, b) => getLatestMessageTime(b) - getLatestMessageTime(a)
    );
    return sorted;
  }, [usersWithChatHistory]);

  // Add prevMessagesRef and useEffect here, after selectedUser is defined
  const prevMessagesRef = useRef<MessageType[]>([]);

  useEffect(() => {
    if (!selectedUser) {
      prevMessagesRef.current = [];
      return;
    }
    const prevMessages = prevMessagesRef.current;
    const currentMessages = selectedUser.messages;
    if (
      currentMessages.length > prevMessages.length &&
      currentMessages[currentMessages.length - 1].direction === "incoming"
    ) {
      fetch(`${config.backendUrl}/users/mark-messages-opened`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({ userId: selectedUser._id }),
      });
    }
    prevMessagesRef.current = currentMessages;
  }, [selectedUser]);

  // Refactor handleSend to accept an optional message argument
  const handleSend = async (msg?: string) => {
    const messageToSend = typeof msg === "string" ? msg : messageInput;
    if (!selectedUser || !messageToSend.trim() || !currentUser) return;
    // Mark all messages as opened for the selected user in local state
    // Update the appropriate list based on current tab
    if (isActiveChat) {
      setUsersWithActiveChat(
        (prev: ChatUser[]) =>
          prev.map((u: ChatUser) =>
            u._id === selectedUser._id
              ? {
                  ...u,
                  messages: u.messages.map((m) => ({ ...m, opened: true })),
                }
              : u
          ) as ChatUser[]
      );
    } else {
      setUsersWithChatHistory(
        (prev: ChatUser[]) =>
          prev.map((u: ChatUser) =>
            u._id === selectedUser._id
              ? {
                  ...u,
                  messages: u.messages.map((m) => ({ ...m, opened: true })),
                }
              : u
          ) as ChatUser[]
      );
    }

    // Also update the backend
    await fetch(`${config.backendUrl}/users/mark-messages-opened`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({ userId: selectedUser._id }),
    });
    const newMessage = {
      message: messageToSend,
      sentTime: new Date().toISOString(),
      sender: currentUser.name || currentUser.username || "admin",
      direction: "outgoing",
    };
    setMessageInput("");

    // Update both lists to keep them in sync
    setUsersWithActiveChat(
      (prev: ChatUser[]) =>
        prev.map((u: ChatUser) =>
          u._id === selectedUser._id
            ? { ...u, messages: [...u.messages, newMessage] }
            : u
        ) as ChatUser[]
    );
    setUsersWithChatHistory(
      (prev: ChatUser[]) =>
        prev.map((u: ChatUser) =>
          u._id === selectedUser._id
            ? { ...u, messages: [...u.messages, newMessage] }
            : u
        ) as ChatUser[]
    );
    try {
      const response = await fetch(
        `${config.backendUrl}/users/send-message-from-chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            phone: selectedUser.phone,
            message: messageToSend,
            sender: currentUser.name || currentUser.username || "admin",
          }),
        }
      );
      if (response.ok) {
        setMessageInput("");
      } else {
        alert("Failed to send message");
      }
    } catch (err) {
      alert("Error sending message");
    } finally {
      // Workaround: Focus the internal textarea of MessageInput after sending
      setTimeout(() => {
        const textarea = document.querySelector(
          ".cs-message-input__content-editor textarea"
        );
        if (textarea) (textarea as HTMLTextAreaElement).focus();
      }, 0);
    }
  };

  // Update handleSendMessageFromEditUser to call handleSend with the message directly
  const handleSendMessageFromEditUser =
    (user: ChatUser) => (message: string) => {
      handleSend(message);
    };

  const handleUserSelect = async (userId: string) => {
    setSelectedUserId(userId);
    if (isMobile) toggleMobileSidebar();
    // Update local state
    setUsersWithActiveChat(
      (prev: ChatUser[]) =>
        prev.map((u: ChatUser) =>
          u._id === userId
            ? {
                ...u,
                messages: u.messages.map((m) => ({ ...m, opened: true })),
              }
            : u
        ) as ChatUser[]
    );

    // Mark all messages as opened in backend
    await fetch(`${config.backendUrl}/users/mark-messages-opened`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
      },
      body: JSON.stringify({ userId }),
    });
  };

  // Add a function to refresh users with active chat
  const refreshActiveChats = async () => {
    const users = await getUsersWithActiveChat();
    setUsersWithActiveChat(users);
  };

  // Handle Attachment button click
  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset so same file can be selected again
      fileInputRef.current.click();
    }
  };

  // Handle file selection and upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedUser?.phone) return;
    setUploadingImage(true);
    try {
      // 1. Upload image
      const formData = new FormData();
      formData.append("phone", selectedUser.phone); // phone first
      formData.append("image", file);
      const uploadRes = await fetch(`${config.backendUrl}/users/upload-image`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: formData,
      });
      const uploadData = await uploadRes.json();
      if (!uploadRes.ok || !uploadData.imagePath) {
        alert(uploadData.message || "Image upload failed");
        setUploadingImage(false);
        return;
      }
      // 2. Send image to user
      const sendRes = await fetch(
        `${config.backendUrl}/users/send-image-to-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            phone: selectedUser.phone,
            imagePath: uploadData.imagePath,
          }),
        }
      );
      const sendData = await sendRes.json();
      if (!sendRes.ok) {
        alert(sendData.message || "Failed to send image");
        setUploadingImage(false);
        return;
      }
      // Optionally, refresh chat or update UI
      // You may want to fetch messages again or optimistically add the image message
      // For now, just refresh users with active chat
      await refreshActiveChats();
    } catch (err) {
      console.log("error uploading image", err);
    } finally {
      setUploadingImage(false);
    }
  };

  const sidebarData = (
    <>
      <div className={styles.usersListTitleContainer}>
        <div
          className={
            styles.usersListTitle + " " + (isActiveChat ? styles.active : "")
          }
          onClick={() => setIsActiveChat(true)}
        >
          שיחות פעילות
        </div>
        <div
          className={
            styles.usersListTitle + " " + (isActiveChat ? "" : styles.active)
          }
          onClick={() => setIsActiveChat(false)}
        >
          היסטוריית שיחות
        </div>
      </div>
      {isActiveChat ? (
        <ConversationList className={styles.conversationList}>
          {usersWithActiveChat.map((user) => {
            const unreadCount = user.messages.filter(
              (msg) => !msg.opened && msg.direction === "incoming"
            ).length;
            return (
              <div
                className={styles.conversationItemContainer}
                key={user._id}
                style={{
                  position: "relative",
                }}
              >
                {user.userWatingForFirstHumanResponse && (
                  <span
                    className={styles.unreadBadge}
                    style={{
                      backgroundColor: "#2ecc40",
                    }}
                  />
                )}
                <Conversation
                  className={
                    selectedUserId === user._id
                      ? styles.conversationItemActive
                      : styles.conversationItem
                  }
                  name={user.name ? user.name : user.phone || ""}
                  info={
                    user.messages && user.messages.length > 0
                      ? user.messages[user.messages.length - 1].message
                      : ""
                  } // last message
                  active={selectedUserId === user._id}
                  style={{
                    backgroundColor:
                      selectedUserId === user._id
                        ? "var(--whatsapp-gray)"
                        : "white",
                  }}
                  onClick={() => handleUserSelect(user._id || "")}
                />
                {unreadCount > 0 && (
                  <div className={styles.unreadBadge}>{unreadCount}</div>
                )}
                <EditButton
                  open={editUserOpenId === user._id}
                  onClick={() => {
                    if (selectedUserId !== user._id && user._id)
                      handleUserSelect(user._id as string);
                    setEditUserOpenId(
                      editUserOpenId === user._id ? null : user._id ?? null
                    );
                  }}
                />
                <EditUser
                  user={user}
                  open={editUserOpenId === user._id}
                  setOpen={(open) =>
                    setEditUserOpenId(open ? user._id ?? null : null)
                  }
                  onChatClosed={refreshActiveChats}
                  setIsActiveChat={setIsActiveChat}
                  handleUserSelect={handleUserSelect}
                  selectedUserId={selectedUserId}
                  setSelectedUserId={setSelectedUserId}
                  onSendMessage={handleSendMessageFromEditUser(user)}
                />
              </div>
            );
          })}
        </ConversationList>
      ) : (
        <ConversationList className={styles.conversationList}>
          <div className={styles.warningContainer}>
            <div className={styles.warningText}>
              אפשר לשלוח הודעות רק בתוך 24 שעות מההודעה האחרונה שהתקבלה מהמשתמש.
            </div>
          </div>

          {sortedHistoryUsers.map((user) => (
            <div className={styles.conversationItemContainer} key={user._id}>
              <Conversation
                className={
                  selectedUserId === user._id
                    ? styles.conversationItemActive
                    : styles.conversationItem
                }
                name={user.name ? user.name : user.phone || ""}
                info={
                  user.messages && user.messages.length > 0
                    ? user.messages[user.messages.length - 1].message
                    : ""
                } // last message
                active={selectedUserId === user._id}
                style={{
                  backgroundColor:
                    selectedUserId === user._id
                      ? "var(--whatsapp-gray)"
                      : "white",
                }}
                onClick={() => {
                  setSelectedUserId(user._id || "");
                  setEditUserOpenId(null);
                  if (isMobile) toggleMobileSidebar();
                }}
              />
              <EditButton
                open={editUserOpenId === user._id}
                onClick={() => {
                  if (selectedUserId !== user._id && user._id)
                    handleUserSelect(user._id as string);
                  setEditUserOpenId(
                    editUserOpenId === user._id ? null : user._id ?? null
                  );
                }}
              />
              <EditUser
                user={user}
                open={editUserOpenId === user._id}
                setOpen={(open) =>
                  setEditUserOpenId(open ? user._id ?? null : null)
                }
                setIsActiveChat={setIsActiveChat}
                handleUserSelect={handleUserSelect}
                selectedUserId={selectedUserId}
                setSelectedUserId={setSelectedUserId}
                onSendMessage={handleSendMessageFromEditUser(user)}
              />
            </div>
          ))}
        </ConversationList>
      )}
    </>
  );
  let pageData = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "80vh",
      }}
    >
      <BarLoader width={200} color="#28a745" />
    </div>
  );
  if (!loading) {
    pageData = (
      <div className={styles.chatPage}>
        <div className={styles.chatWrapper}>
          {/* Hidden file input for image upload */}
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          {/* 
          {mobileSidebarOpen && (
            <div className={styles.MobileSidebar}>{sidebarData}</div>
          )} */}
          <div
            style={{
              opacity: mobileSidebarOpen ? "1" : "0",
              height: mobileSidebarOpen ? "auto" : "10px",
            }}
            className={styles.MobileSidebar}
          >
            {sidebarData}
          </div>
          <MainContainer responsive className={styles.mainContainer}>
            <Sidebar position="left" scrollable className={styles.sidebar}>
              {sidebarData}
            </Sidebar>

            <ChatContainer
              style={{
                left: isMobile && mobileSidebarOpen ? "100%" : "0",
              }}
              className={styles.chatContainer}
            >
              {selectedUser && (
                <InputToolbox className={styles.inputToolbox}>
                  <MessageInput
                    className={styles.messageInput}
                    placeholder="כתוב הודעה כאן"
                    value={messageInput}
                    onChange={setMessageInput}
                    onSend={handleSend}
                    // disabled={
                    //   !selectedUser || !currentUser || uploadingImage
                    // }
                    autoFocus={true}
                    attachButton={false}
                  />

                  <AttachmentButton
                    className={styles.attachmentButton}
                    onClick={handleAttachmentClick}
                    disabled={uploadingImage}
                  ></AttachmentButton>
                  {uploadingImage && (
                    <PuffLoader
                      color="#28a745"
                      size={20}
                      className={styles.uploadingImage}
                    />
                  )}
                </InputToolbox>
              )}

              <MessageList dir="rtl" className={styles.messageList}>
                {messages.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#888",
                      marginTop: 24,
                    }}
                  >
                    לא נמצאו הודעות
                  </div>
                ) : (
                  messages.map((msg: MessageType, idx: number) => {
                    const showDateHeader =
                      idx === 0 ||
                      getDateKey(msg.sentTime) !==
                        getDateKey(messages[idx - 1].sentTime);
                    return (
                      <React.Fragment key={msg._id ?? idx}>
                        {showDateHeader && (
                          <div className={styles.dateSeparator}>
                            <span className={styles.dateSeparatorPill}>
                              {formatMessageDate(msg.sentTime)}
                            </span>
                          </div>
                        )}
                        <LongPressMessageWrapper
                          enabled={isMobile}
                          onLongPress={(rect) =>
                            setActionAnchor({ rect, msg })
                          }
                        >
                          <Message
                            model={{
                              message: msg.message,
                              sentTime: msg.sentTime,
                              sender: msg.sender,
                              direction: msg.direction,
                              position: "single",
                            }}
                            className={
                              msg.direction === "outgoing"
                                ? styles.outgoingMessage
                                : ""
                            }
                          >
                            {msg.imagePath && (
                              <Message.CustomContent
                                className={styles.imageContainer}
                              >
                                <img
                                  src={`${config.backendUrl}${msg.imagePath}`}
                                  alt="תמונה מהמשתמש"
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                    marginTop: 8,
                                    borderRadius: 8,
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setEnlargedImage(
                                      `${config.backendUrl}${msg.imagePath}`
                                    )
                                  }
                                />
                              </Message.CustomContent>
                            )}
                            <Message.Footer
                              className={
                                msg.direction === "outgoing"
                                  ? `${styles.messageDateTime} ${styles.messageDateTimeOutgoing}`
                                  : styles.messageDateTime
                              }
                            >
                              {formatMessageTime(msg.sentTime)}
                            </Message.Footer>
                          </Message>
                        </LongPressMessageWrapper>
                      </React.Fragment>
                    );
                  })
                )}
              </MessageList>
            </ChatContainer>
          </MainContainer>
        </div>
        <MessageActionMenu
          open={!!actionAnchor}
          anchorRect={actionAnchor?.rect ?? null}
          onClose={() => setActionAnchor(null)}
          actions={
            actionAnchor
              ? [
                  {
                    id: "copy",
                    label: "העתק טקסט",
                    disabled: !actionAnchor.msg.message,
                    onSelect: async () => {
                      await copyToClipboard(actionAnchor.msg.message ?? "");
                    },
                  },
                ]
              : ([] as MessageAction[])
          }
        />
        {/* Image Popup Modal */}
        {enlargedImage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgb(255, 255, 255)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              cursor: "zoom-out",
              flexDirection: "column",
            }}
            onClick={() => setEnlargedImage(null)}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                padding: "16px 24px 0 0",
                boxSizing: "border-box",
                position: "absolute",
                top: 0,
                left: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setEnlargedImage(null)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 28,
                  fontWeight: 600,
                  cursor: "pointer",
                  color: "#333",
                  lineHeight: 1,
                  padding: 0,
                }}
                aria-label="סגור"
              >
                ×
              </button>
            </div>
            <img
              src={enlargedImage}
              alt="Enlarged"
              className={`${styles.enlargedImageEnter} ${
                showImage ? styles.enlargedImageEnterActive : ""
              }`}
              style={{
                maxWidth: "90vw",
                maxHeight: "90vh",
                borderRadius: 12,
                boxShadow: "0 2px 16px rgba(0,0,0,0.5)",
                background: "#fff",
                cursor: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }
  return pageData;
};

export default Chat;
